import { Contact, Settings, User } from "lucide-react";
import React, { useState } from "react";
import { cn } from 'utils/cn';
import { useResponsive } from 'utils/useMediaQuery';
import CopyButton from "../CopyButton";
import { DataStateOverlay } from "../DataLoadOverlay";
import Label from "../Label";
import { Option } from "../MultiSelect";
import ResponsiveDialog from "../ResponsiveDialog";
import { ToggleGroup, ToggleGroupItem } from "../ToggleGroup";
import { AccessObject } from "./access-option";
import { PeopleProps } from "./access-people-table";
import CollaboratorClient from "./collaborator";
import GuestClient from "./guest-client";
import ManageAccess from "./manage-access";
import TeamMembers from "./team-members";


export type ShareProjectModalProps = {
    container?: HTMLElement | ShadowRoot | null,
    users: UserOption[],
    handleMultiSelectChange: (options: Option[]) => void,
    multiSelectValue: UserOption[] | undefined,
    handleChangeShareNote: React.ChangeEventHandler<HTMLTextAreaElement> | undefined,
    shareNote: string | number | readonly string[] | undefined,
    userRoles: RoleOption[],
    roleValue: Option,
    handleSelectRole: (roleValue: Option) => void,
    handleSubmitTeamMembers: () => void,
    // guest props types
    handleGuestMultiSelectOnChange: (options: Option[]) => void,
    multiSelectguestValue: Option[] | undefined,
    handleChangeGuestNote: React.ChangeEventHandler<HTMLTextAreaElement> | undefined,
    guestNote: string | number | readonly string[] | undefined,
    handleInviteGuest: () => void,
    // manage option types
    accessOptions: AccessObject[],
    handleAccessLevelChange: (accessLevel: AccessObject) => void,
    accessLevel: AccessObject,
    people: PeopleProps[],
    onDelete: (id: string | number, role: string) => void,
    onResendInvite: (id: string | number) => void,
    inviteTeamLink: string,
    collabLink: string,
    websiteUserRole?: string,
    isInvitingMember: boolean,
    isLoadingContributors: boolean,
    defaultTab?: ShareProjectModalTab,
    inviteMoreStatus: boolean,
    userCount: number | string | undefined,
    teamPrice: string | number | undefined,
    startingCheckout?: boolean,
    basePrice?: string | number,
    additionalSeatPrice?: string,
    currentInterval?: string,
    plan?: string,
    //
    className?: string
}

export type ShareProjectModalTab = 'team' | 'guests' | 'access';

export type UserOption = Option & {
    src?: string,
    email?: string,
    selected?: boolean,
}
export type RoleOption = Option & {
    icon?: React.JSX.Element,
}


export default function ShareProjectModal({
    container,
    users,
    handleMultiSelectChange,
    multiSelectValue,
    handleChangeShareNote,
    shareNote,
    userRoles,
    roleValue,
    handleSelectRole,
    handleSubmitTeamMembers,
    handleGuestMultiSelectOnChange,
    multiSelectguestValue,
    handleChangeGuestNote,
    guestNote,
    handleInviteGuest,
    accessOptions,
    handleAccessLevelChange,
    accessLevel,
    people,
    onDelete,
    onResendInvite,
    inviteTeamLink,
    collabLink,
    websiteUserRole,
    isInvitingMember,
    isLoadingContributors,
    defaultTab = 'team',
    inviteMoreStatus = false,
    userCount,
    teamPrice,
    startingCheckout = false,
    basePrice,
    additionalSeatPrice,
    currentInterval,
    plan,
    className
}: ShareProjectModalProps) {
    const [activeTab, setActiveTab] = useState<ShareProjectModalTab>(defaultTab ?? 'team');
    const { isTablet, isDesktop, isMobile, isLargeDesktop } = useResponsive();
    return (
        <ResponsiveDialog.Content container={container} className={`flex flex-col text-textcolor isolate max-w-[600px] ${className}`}
            onOpenAutoFocus={(e: { preventDefault: () => any; }) => e.preventDefault()}
            onInteractOutside={(event: { preventDefault: () => any; }) => event.preventDefault()}
        >
            <div className="relative">
                <DataStateOverlay visible={isLoadingContributors} className="" />
                <ResponsiveDialog.Header className="flex flex-col gap-1 p-2 space-y-0">
                    <ResponsiveDialog.Title className="block">
                        {
                            websiteUserRole === 'collaborator' || websiteUserRole === 'guest' ?
                                "Work Together on This Project" :
                                "Collaborate on this project"
                        }
                    </ResponsiveDialog.Title>

                    <div className="flex justify-between items-center gap-1 md:gap-2">
                        <Label htmlFor="site-name" className="text-left text-sm font-normal h-9 items-center flex ">
                            Invite others to share their thoughts on this project.
                        </Label>
                        {
                            activeTab !== 'access' && (
                                <CopyButton
                                    text={collabLink}
                                    variant={websiteUserRole === 'collaborator' || websiteUserRole === 'guest' ? "outline" : (isMobile && websiteUserRole !== 'guest' ? 'outline' : 'ghost')}
                                    className={cn(websiteUserRole !== 'collaborator' ? 'text-tertiary' : 'text-textcolor', isMobile && 'gap-0')}
                                    size='sm'
                                    buttonText={cn(!isMobile && 'Copy Link')}
                                    tooltip={websiteUserRole === 'collaborator' || websiteUserRole === 'guest' ? "Copy and share a quick link to this project" : 'Copy a quick link to this project'}
                                    tooltipSide='bottom'
                                    tooltipAlign="end"
                                />
                            )
                        }
                    </div>
                </ResponsiveDialog.Header>
                {
                    websiteUserRole === 'collaborator' || websiteUserRole === 'guest' ?
                        <CollaboratorClient
                            handleGuestMultiSelectOnChange={handleGuestMultiSelectOnChange}
                            multiSelectguestValue={multiSelectguestValue}
                            handleChangeGuestNote={handleChangeGuestNote}
                            guestNote={guestNote}
                            handleInviteGuest={handleInviteGuest}
                            collabLink={collabLink}
                            inviteTeamLink={inviteTeamLink}
                            isInvitingMember={isInvitingMember}
                        /> :
                        <div className="mt-4 px-2">
                            <ToggleGroup
                                className="w-full rounded-lg p-0 justify-start"
                                type="single"
                                value={activeTab}
                                onValueChange={(value: string) => {
                                    if (!value) return;
                                    setActiveTab(value as 'team' | 'guests' | 'access')
                                }
                                }
                            >
                                <ToggleGroupItem
                                    variant="tertiary"
                                    className=" border-none font-normal"
                                    value="team"
                                    size="sm"
                                >
                                    <User size={18} />
                                    {isMobile ? 'Team' : 'Team Members'}
                                </ToggleGroupItem>
                                <ToggleGroupItem
                                    variant="tertiary"
                                    className=" border-none font-normal"
                                    value="guests"
                                    size="sm"
                                >
                                    <Contact size={18} /> Guests and Clients
                                </ToggleGroupItem>
                                {
                                    websiteUserRole === 'admin' &&
                                    <ToggleGroupItem
                                        variant="tertiary"
                                        className=" border-none font-normal"
                                        value="access"
                                        size="sm"
                                    >
                                        <Settings size={18} />
                                        {isMobile ? 'Manage' : 'Manage Access'}

                                    </ToggleGroupItem>
                                }
                            </ToggleGroup>
                            {activeTab === 'team' &&
                                <TeamMembers
                                    users={users}
                                    handleMultiSelectOnChange={handleMultiSelectChange}
                                    multiSelectValue={multiSelectValue}
                                    handleChangeShareNote={handleChangeShareNote}
                                    shareNote={shareNote}
                                    handleSelectRole={handleSelectRole}
                                    roleValue={roleValue}
                                    userRoles={userRoles}
                                    handleSubmitTeamMembers={handleSubmitTeamMembers}
                                    inviteTeamLink={inviteTeamLink}
                                    isInvitingMember={isInvitingMember}
                                    websiteUserRole={websiteUserRole}
                                    inviteMoreStatus={inviteMoreStatus}
                                    userCount={userCount}
                                    teamPrice={teamPrice}
                                    startingCheckout={startingCheckout}
                                    basePrice={basePrice}
                                    additionalSeatPrice={additionalSeatPrice}
                                    currentInterval={currentInterval}
                                    plan={plan}
                                />}
                            {activeTab === 'guests' &&
                                <GuestClient
                                    handleGuestMultiSelectOnChange={handleGuestMultiSelectOnChange}
                                    multiSelectguestValue={multiSelectguestValue}
                                    handleChangeGuestNote={handleChangeGuestNote}
                                    guestNote={guestNote}
                                    handleInviteGuest={handleInviteGuest}
                                    isInvitingMember={isInvitingMember}
                                />}
                            {activeTab === 'access' && websiteUserRole === 'admin' &&
                                <ManageAccess
                                    accessOptions={accessOptions}
                                    handleAccessLevelChange={handleAccessLevelChange}
                                    accessLevel={accessLevel}
                                    people={people}
                                    onDelete={onDelete}
                                    onResendInvite={onResendInvite}
                                    container={container}
                                />}
                        </div>
                }
            </div>
        </ResponsiveDialog.Content>
    );
}